import { Questionnaire, QuestionnaireQuestion } from "../../Models/Surveys/SurveyDataModel";

interface QuestionAndIndex {
    question: QuestionnaireQuestion;
    index: number;
}

/**
 * Evaluator for determing if a questionnaire question is required or not
 */
export abstract class QuestionRequirementEvaluator {
    protected _questions: QuestionnaireQuestion[];
    private _overallQuestionnaire: Questionnaire;

    constructor(allQuestionsOnPage: QuestionnaireQuestion[], overallQuestionnaire: Questionnaire) {
        this._questions = allQuestionsOnPage;
        this._overallQuestionnaire = overallQuestionnaire;
    }

    protected getChildQuestions(questionIndex: number, allQuestionsOnpage: QuestionAndIndex[], visitedSet = new Set<number>()): QuestionAndIndex[] {
        if (visitedSet.has(questionIndex)) {
            return [];
        }

        visitedSet.add(questionIndex);

        let childQuestions = allQuestionsOnpage.filter((q, i) => (i !== questionIndex && q.question.conditionalOnQuestion?.questionIndex === questionIndex));

        if (childQuestions.length === 0) {
            return [];
        }

        let ret: QuestionAndIndex[] = childQuestions;

        for (let c of childQuestions) {
            ret.unshift(...this.getChildQuestions(c.index, allQuestionsOnpage, visitedSet));
        }

        return ret;
    }

    protected getParentQuestion(question: QuestionnaireQuestion) {
        if (question.conditionalOnQuestion?.pageType) {
            return this._overallQuestionnaire.pages.firstOrDefault(p => p.type == question.conditionalOnQuestion?.pageType)?.questions[question.conditionalOnQuestion.questionIndex];
        }
        else {
            return this._questions[question.conditionalOnQuestion?.questionIndex]
        }
    }

    /**
     * Standard way to determine if a question is required without using any custom logic.
     * 
     * @param question
     * @returns
     */
    public isQuestionRequired(
        question: QuestionnaireQuestion
    ): boolean {
        const parentQuestion = this.getParentQuestion(question);

        const conditionallyRequired: boolean =
            ((question.conditionalOnQuestion !== null) && (parentQuestion !== undefined)) &&
            (question.conditionalOnQuestion.answers.some(answer => answer == parentQuestion.value));

        let parentQuestionRequired = false;

        if (conditionallyRequired && parentQuestion) {
            parentQuestionRequired = this.isQuestionRequired(parentQuestion) ?? false;
        }

        const standardRequired: boolean =
            (question.isRequired || conditionallyRequired) &&
            ((!question.conditionalOnQuestion?.requireOnlyIfParentIsRequired) || (question.conditionalOnQuestion?.requireOnlyIfParentIsRequired && parentQuestionRequired));

        return standardRequired;
    }
}

export class GenericQuestionRequirementEvaluator extends QuestionRequirementEvaluator { }